import { createBrowserRouter, Navigate, useParams } from "react-router-dom";

// Public pages
import Home              from "@/pages/public/Home";
import About             from "@/pages/public/About";
import Contact           from "@/pages/public/Contact";
import Pricing           from "@/pages/public/Pricing";
import Terms             from "@/pages/public/Terms";
import Privacy           from "@/pages/public/Privacy";
import SchoolDataAgreement from "@/pages/public/SchoolDataAgreement";
import DemoPicker        from "@/pages/public/DemoPicker";
import DemoReportEditor  from "@/pages/public/DemoReportEditor";

// Private pages
import Dashboard         from "@/pages/private/Dashboard";
import ReportFolders     from "@/pages/private/ReportFolders";
import { OwnedReportFolder } from "@/pages/private/OwnedReportFolder";
import { ReportCard }    from "@/pages/private/ReportCard";
import { ReportDefaults } from "@/pages/private/ReportDefaults";
import { Branding }       from "@/pages/private/Branding";
import { SignatureSettings } from "@/pages/private/SignatureSettings";
import SharedReportFolders from "@/pages/private/SharedReportFolders";
import SharedReportFolder from "@/pages/private/SharedReportFolder";
import CreditsAndBilling from "@/pages/private/CreditsAndBilling";
import School            from "@/pages/private/School";
import Gradebooks        from "@/pages/private/Gradebooks";
import Gradebook         from "@/pages/private/Gradebook";
import GradebookAssessments from "@/pages/private/GradebookAssessments";
import GradebookInsights from "@/pages/private/GradebookInsights";
import GradebookLayout   from "@/pages/private/GradebookLayout";

// Admin pages
import SchoolActivity              from "@/pages/private/admin/SchoolActivity";
import ManageReportFolderOwnership from "@/pages/private/admin/ManageReportFolderOwnership";
import ManageReportFolderSharing   from "@/pages/private/admin/ManageReportFolderSharing";
import AdminSettings               from "@/pages/private/admin/Settings";
import SeatManagement              from "@/pages/private/admin/SeatManagement";
import ReportFolderShares          from "@/pages/private/admin/ReportFolderShares";
import Members                     from "@/pages/private/admin/Members";
import SignatureQueue              from "@/pages/private/admin/SignatureQueue";

// Layouts
import RootLayout      from "@/layouts/root-layout";
import DashboardLayout from "@/layouts/dashboard-layout";
import PublicLayout    from "@/layouts/public-layout";
import RequireAdmin    from "@/layouts/RequireAdmin";
import NotFound        from "@/pages/NotFound";

// eslint-disable-next-line react-refresh/only-export-components
function LegacyDemoRedirect() {
  const { reportType } = useParams();
  return <Navigate to={`/demo/${reportType}`} replace />;
}

export const router = createBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,
    children: [
      {
        element: <PublicLayout />,
        children: [
          { path: "/",             element: <Home /> },
          { path: "/about",        element: <About /> },
          { path: "/contact",      element: <Contact /> },
          { path: "/pricing",      element: <Pricing /> },
          { path: "/terms",         element: <Terms /> },
          { path: "/privacy",       element: <Privacy /> },
          { path: "/school-data-agreement", element: <SchoolDataAgreement /> },
          { path: "/demo",                   element: <DemoPicker /> },
          { path: "/demo/:reportType",       element: <DemoReportEditor /> },
          // Legacy demo routes → redirect to current paths
          { path: "/free-trial",             element: <Navigate to="/demo" replace /> },
          { path: "/free-trial/:reportType", element: <LegacyDemoRedirect /> },
          { path: "/pgrc",         element: <Navigate to="/demo/pgrc1to6_2023_07" replace /> },
          { path: "/pgrc78",       element: <Navigate to="/demo/pgrc7to8_2023_07" replace /> },
          { path: "/pvrc",         element: <Navigate to="/demo/pvrc1to6_2023_09" replace /> },
          { path: "/pvrc78",       element: <Navigate to="/demo/pvrc7to8_2023_09" replace /> },
          { path: "/pvrc9to12sem", element: <Navigate to="/demo/pvrc9to12_2022_02_sem" replace /> },
        ],
      },
      {
        element: <DashboardLayout />,
        children: [
          { path: "/dashboard",                                                       element: <Dashboard /> },
          { path: "/report-folders",                                                  element: <ReportFolders /> },
          { path: "/shared-report-folders",                                           element: <SharedReportFolders /> },
          { path: "/shared-report-folders/:sharedReportFolderId",                     element: <SharedReportFolder /> },
          { path: "/shared-report-folders/:sharedReportFolderId/reports/:id",         element: <ReportCard /> },
          { path: "/report-folders/:reportFolderId",                                  element: <OwnedReportFolder /> },
          { path: "/report-folders/:reportFolderId/defaults",                         element: <ReportDefaults /> },
          { path: "/report-folders/:reportFolderId/branding",                         element: <Branding /> },
          { path: "/report-folders/:reportFolderId/reports/:id",                      element: <ReportCard /> },
          // Gradebook
          { path: "/gradebooks",                                                      element: <Gradebooks /> },
          /* NESTED, not three siblings. `GradebookLayout` fetches the book once
             and stays mounted for as long as the URL is inside it, so moving
             between the sheet, the assessments and the insights re-uses the same
             data instead of re-downloading every mark in the book. See the file
             for the full lifetime rules. */
          {
            path: "/gradebooks/:gradebookId",
            element: <GradebookLayout />,
            children: [
              { index: true,          element: <Gradebook /> },
              { path: "assessments",  element: <GradebookAssessments /> },
              { path: "insights",     element: <GradebookInsights /> },
            ],
          },
          { path: "/credits-and-billing",                                             element: <CreditsAndBilling /> },
          { path: "/settings/signature",                                              element: <SignatureSettings /> },
          { path: "/usage",                                                           element: <Navigate to="/credits-and-billing" replace /> },
          { path: "/billing",                                                         element: <Navigate to="/credits-and-billing" replace /> },
          // School routes
          { path: "/schools",                  element: <School /> },
          // Admin-only school routes
          {
            element: <RequireAdmin />,
            children: [
              { path: "/schools/activity",                            element: <SchoolActivity /> },
              { path: "/schools/members",                             element: <Members /> },
              { path: "/schools/settings",                            element: <AdminSettings /> },
              { path: "/schools/report-folders",                      element: <ManageReportFolderOwnership /> },
              { path: "/schools/report-folders/:reportFolderId",      element: <ManageReportFolderOwnership /> },
              { path: "/schools/share-requests",                      element: <ManageReportFolderSharing /> },
              { path: "/schools/signature-queue",                     element: <SignatureQueue /> },
              { path: "/schools/seats",                               element: <SeatManagement /> },
              { path: "/schools/report-folder-shares",                element: <ReportFolderShares /> },
            ],
          },
        ],
      },
      { path: "*", element: <NotFound /> },
    ],
  },
]);
